<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import { useCalculationStore } from '@/stores/calculation'
import type { CalculationComputeResource } from '@/stores/type/calculation.type'

const props = defineProps<{
  modelValue: boolean
  calculationId: number
  resource: CalculationComputeResource
  resourceLabel?: string
  autoRefreshEnabled?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const calculationStore = useCalculationStore()

const AUTO_REFRESH_INTERVAL_MS = 5000
const REFRESH_PROGRESS_INTERVAL_MS = 100

const dialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

const consoleRef = ref<HTMLElement>()
const logs = ref('')
const loading = ref(false)
const autoRefresh = ref(true)
const lastRefreshed = ref<Date | null>(null)
const refreshProgress = ref(0)
let refreshProgressIntervalId: number | undefined
let refreshProgressStartedAt = 0

const isDownloadingLogs = computed(() => {
  return calculationStore.computeResourceLogDownloadLoading === props.resource.name
})

const hasLogs = computed(() => logs.value.trim().length > 0)

const canDownloadLogs = computed(() => {
  return Boolean(props.calculationId && props.resource.name && hasLogs.value)
})

const lastRefreshedText = computed(() => {
  if (!lastRefreshed.value) return ''
  return lastRefreshed.value.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  })
})

const title = computed(() => {
  const label = props.resourceLabel ?? 'Compute resource'
  return `${label} logs`
})

const scrollToBottom = async () => {
  await nextTick()
  if (!consoleRef.value) return
  consoleRef.value.scrollTop = consoleRef.value.scrollHeight
}

const mergeLogs = (nextLogs: string) => {
  if (nextLogs.startsWith(logs.value)) {
    logs.value += nextLogs.slice(logs.value.length)
  } else {
    logs.value = nextLogs
  }
  scrollToBottom()
}

const refreshLogs = async () => {
  if (loading.value || !props.calculationId || !props.resource.name) return

  loading.value = true
  try {
    const response = await calculationStore.getComputeResourceLogs(
      props.calculationId,
      props.resource.name,
      500
    )
    if (response) {
      mergeLogs(response.logs)
      lastRefreshed.value = new Date()
    }
  } finally {
    loading.value = false
    restartRefreshProgress()
  }
}

const stopAutoRefresh = () => {
  stopRefreshProgress()
}

const startAutoRefresh = () => {
  stopAutoRefresh()
  if (!dialog.value || !autoRefresh.value || props.autoRefreshEnabled === false) return
  restartRefreshProgress()
}

const stopRefreshProgress = () => {
  if (refreshProgressIntervalId) {
    window.clearInterval(refreshProgressIntervalId)
    refreshProgressIntervalId = undefined
  }
  refreshProgress.value = 0
}

const restartRefreshProgress = () => {
  if (!dialog.value || !autoRefresh.value || props.autoRefreshEnabled === false) return
  if (refreshProgressIntervalId) {
    window.clearInterval(refreshProgressIntervalId)
  }

  refreshProgressStartedAt = Date.now()
  refreshProgress.value = 0
  refreshProgressIntervalId = window.setInterval(() => {
    const elapsed = Date.now() - refreshProgressStartedAt
    refreshProgress.value = Math.min(100, (elapsed / AUTO_REFRESH_INTERVAL_MS) * 100)
    if (elapsed >= AUTO_REFRESH_INTERVAL_MS) {
      stopRefreshProgress()
      refreshLogs()
    }
  }, REFRESH_PROGRESS_INTERVAL_MS)
}

const close = () => {
  dialog.value = false
}

const downloadLogs = async () => {
  if (!canDownloadLogs.value || isDownloadingLogs.value) return
  await calculationStore.downloadComputeResourceLogs(props.calculationId, props.resource.name)
}

watch(
  dialog,
  async (value) => {
    if (!value) {
      stopAutoRefresh()
      return
    }

    logs.value = ''
    lastRefreshed.value = null
    await refreshLogs()
    startAutoRefresh()
  },
  { immediate: true }
)

watch(autoRefresh, startAutoRefresh)

watch(
  () => props.autoRefreshEnabled,
  (enabled) => {
    if (enabled === false) {
      stopAutoRefresh()
      return
    }
    startAutoRefresh()
  }
)

watch(
  () => props.resource.name,
  async () => {
    if (!dialog.value) return
    logs.value = ''
    lastRefreshed.value = null
    await refreshLogs()
    startAutoRefresh()
  }
)

onBeforeUnmount(stopAutoRefresh)
</script>

<template>
  <v-dialog v-model="dialog" max-width="1100" @click:outside="close">
    <v-card class="container-logs-dialog">
      <v-toolbar density="compact" color="transparent">
        <v-toolbar-title class="logs-title text-subtitle-1 font-weight-medium">
          {{ title }}: {{ resource.name }}
        </v-toolbar-title>
        <span v-if="lastRefreshedText" class="text-caption text-medium-emphasis mr-3">
          {{ lastRefreshedText }}
        </span>
        <v-switch
          v-model="autoRefresh"
          class="mr-2"
          color="primary"
          density="compact"
          :disabled="autoRefreshEnabled === false"
          hide-details
          label="Auto refresh"
        />
        <v-btn icon="mdi-refresh" variant="text" :loading="loading" @click="refreshLogs" />
        <v-btn
          icon="mdi-download"
          variant="text"
          :disabled="!canDownloadLogs || isDownloadingLogs"
          :loading="isDownloadingLogs"
          @click="downloadLogs"
        />
        <v-btn icon="mdi-close" variant="text" @click="close" />
      </v-toolbar>

      <v-progress-linear
        :indeterminate="loading"
        :model-value="autoRefresh && autoRefreshEnabled !== false ? refreshProgress : 0"
        color="primary"
        height="3"
      />

      <v-divider />

      <v-card-text class="pa-0">
        <div ref="consoleRef" class="logs-console">
          <pre v-if="logs">{{ logs }}</pre>
          <div v-else-if="loading" class="logs-empty">Loading logs...</div>
          <div v-else class="logs-empty">No logs available.</div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.container-logs-dialog {
  overflow: hidden;
}

.logs-title {
  min-width: 0;
}

.logs-console {
  background: #101214;
  color: #e9edf1;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 0.86rem;
  height: min(68vh, 720px);
  line-height: 1.45;
  overflow: auto;
  padding: 16px;
}

.logs-console pre {
  font: inherit;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.logs-empty {
  align-items: center;
  color: #9ba4ad;
  display: flex;
  height: 100%;
  justify-content: center;
}
</style>
